import type { Comment, CommentListOrder, CommentTargetType } from '@/@types/comment'
import type { ContentType } from '@/@types/common'
import { GET_COMMENT, LIST_COMMENTS, LIST_PRIMARY_COMMENTS } from '@/constants/api'
import type {
	InfiniteData,
	UseInfiniteQueryOptions,
	UseSuspenseQueryOptions,
} from '@tanstack/react-query'
import { pick } from 'lodash-es'
import queryClient from '../queryClient'
import { get, post } from '../request'

/**
 * ***************** Comment 👇 *****************
 */

export interface GetCommentParams {
	id: string
	targetType: ContentType
}

export function generateCommentQueryKey(params: GetCommentParams) {
	return [GET_COMMENT, params]
}

export async function getComment(params: GetCommentParams): Promise<Comment> {
	return await get(GET_COMMENT, {
		params,
	}).then((res) => res.data.data)
}

export function getCommentQueryOptions(params: GetCommentParams): UseSuspenseQueryOptions<Comment> {
	return {
		queryKey: generateCommentQueryKey(params),
		queryFn: () => getComment(params),
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
		retryOnMount: false,
	}
}

/**
 * ***************** Comment List 👇 *****************
 */

export interface QueryCommentsParams {
	targetId: string
	targetType: CommentTargetType
	primaryCommentId?: string
	level?: number
	limit?: number
	order?: CommentListOrder
}

interface QueryCommentsPage {
	data: Comment[]
	loadMoreKey?: unknown
}

export function generatePrimaryCommentsQueryKey(params: QueryCommentsParams) {
	return [LIST_PRIMARY_COMMENTS, params]
}

export function generateNonPrimaryCommentsQueryKey(params: QueryCommentsParams) {
	return [LIST_COMMENTS, params]
}

export function getPrimaryCommentsQueryOptions(
	params: QueryCommentsParams
): UseInfiniteQueryOptions<QueryCommentsPage, unknown, InfiniteData<QueryCommentsPage>> {
	return {
		queryKey: generatePrimaryCommentsQueryKey(params),
		queryFn: ({ pageParam }) =>
			post<QueryCommentsPage>(LIST_PRIMARY_COMMENTS, {
				limit: 20,
				loadMoreKey: pageParam,
				...params,
			}).then((res) => {
				const body = res.data

				// cache comments
				for (const item of body.data) {
					queryClient.setQueryData(generateCommentQueryKey(pick(item, ['id', 'targetType'])), item)
				}

				return body
			}),
		getNextPageParam: (lastPage: QueryCommentsPage) => lastPage.loadMoreKey,
		initialPageParam: undefined,
		initialData: {
			pages: [],
			pageParams: [],
		},
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
		retryOnMount: false,
	}
}

export function getNonPrimaryCommentsQueryOptions(
	params: QueryCommentsParams
): UseInfiniteQueryOptions<QueryCommentsPage, unknown, InfiniteData<QueryCommentsPage>> {
	return {
		queryKey: generateNonPrimaryCommentsQueryKey(params),
		queryFn: ({ pageParam }) =>
			post<QueryCommentsPage>(LIST_COMMENTS, {
				limit: 20,
				loadMoreKey: pageParam,
				...params,
			}).then((res) => {
				const body = res.data

				// cache comments
				for (const item of body.data) {
					queryClient.setQueryData(generateCommentQueryKey(pick(item, ['id', 'targetType'])), item)
				}

				return body
			}),
		getNextPageParam: (lastPage: QueryCommentsPage) => lastPage.loadMoreKey,
		initialPageParam: undefined,
		initialData: {
			pages: [],
			pageParams: [],
		},
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
		retryOnMount: false,
	}
}
